import {
  Landmark,
  Paperclip,
  Text,
  User,
  FileText,
  CircleCheckBig,
  FileUser,
  HandCoins,
  BadgeDollarSign,
  CircleDollarSign,
  Hash,
  TextSearch,
} from "lucide-react";
import {
  ConditionSeparator,
  ContactDetail,
  ContractHeader,
  Detail,
} from "./ContractDetails";
import { formatCurrency, toPascalCase } from "../../utils/helpers";
import { FaCity } from "react-icons/fa";
import { Attachments } from "./Attachments";
import { Separator } from "../../components/ui/separator";
import { ContractContext } from "./ContractDescription";
import { useContext } from "react";
import { Recommendations } from "./Recommendations";

export const extractGrantData = (data) => ({
  createdDate: data?.createdDate || null,
  postingDate: data?.postingDate || null,
  lastUpdatedDate: data?.lastUpdatedDate || null,
  responseDate: data?.responseDate || null,
  estSynopsisPostingDate: data?.estSynopsisPostingDate || null,
  estApplicationResponseDate: data?.estApplicationResponseDate || null,
  description: data?.synopsisDesc || data?.forecastDesc || null,
  fundingInstruments: data?.fundingInstruments || [],
  fundingActivityCategories: data?.fundingActivityCategories || [],
  applicantTypes: data?.applicantTypes || [],
  awardCeiling: data?.awardCeiling || null,
  awardFloor: data?.awardFloor || null,
  numberOfAwards: data?.numberOfAwards || null,
  agencyContactName: data?.agencyContactName || null,
  agencyContactEmail: data?.agencyContactEmail || null,
  agencyContactPhone: data?.agencyContactPhone || null,
});

export default function GrantContract({ contract }) {
  const {
    agencyDetails = null,
    docType = null,
    forecast = null,
    synopsis = null,
    opportunityCategory = null,
    synopsisAttachmentFolders = null,
    cfdas = [],
  } = contract || {};

  const {
    createdDate,
    lastUpdatedDate,
    responseDate,
    postingDate,
    estSynopsisPostingDate,
    estApplicationResponseDate,
    description,
    fundingInstruments,
    fundingActivityCategories,
    applicantTypes,
    awardCeiling,
    awardFloor,
    numberOfAwards,
    agencyContactName,
    agencyContactEmail,
    agencyContactPhone,
  } = synopsis ? extractGrantData(synopsis) : extractGrantData(forecast);
  const { isLoading } = useContext(ContractContext);

  const headerData = [
    {
      title: "Buyer",
      icon: Landmark,
      details: [toPascalCase(agencyDetails)],
    },

    { title: "Type", icon: FileText, details: [toPascalCase(docType)] },

    {
      title: "Category",
      icon: CircleCheckBig,
      details: [toPascalCase(opportunityCategory)],
    },

    { title: "Number of Awards", icon: Hash, details: [numberOfAwards] },

    {
      title: "Award Floor",
      icon: BadgeDollarSign,
      details: [formatCurrency(awardFloor)],
    },
    {
      title: "Award Ceiling",
      icon: CircleDollarSign,
      details: [formatCurrency(awardCeiling)],
    },
  ];

  const timelineData = [
    {
      title: "Posting Date",
      date: postingDate,
    },
    {
      title: "Estimated Synopsis Posting Date",
      date: estSynopsisPostingDate,
    },
    {
      title: "Estimated Application Response Date",
      date: estApplicationResponseDate,
    },
    {
      title: "Last Updated",
      date: lastUpdatedDate,
    },
    {
      title: "Due Date",
      date: responseDate,
    },
  ];

  return (
    <dl>
      <div id="contract-details" className="flex flex-col gap-4 py-4 ">
        <ContractHeader headerData={headerData} timelineData={timelineData} />

        {ConditionSeparator(isLoading, cfdas)}
        <Detail
          title="CFDA"
          icon={FaCity}
          details={cfdas.map(
            (cfda) => cfda.cfdaNumber + " - " + cfda.programTitle
          )}
        />
        {ConditionSeparator(
          isLoading,
          fundingInstruments,
          fundingActivityCategories,
          applicantTypes
        )}
        <Detail
          title="Funding Instruments"
          icon={HandCoins}
          details={fundingInstruments}
        />
        <Detail
          title="Funding Categories"
          icon={HandCoins}
          details={fundingActivityCategories}
        />
        <Detail
          title="Eligible Applicants"
          icon={FileUser}
          details={applicantTypes}
        />
        {ConditionSeparator(
          isLoading,
          agencyContactName,
          agencyContactEmail,
          agencyContactPhone
        )}
        <ContactDetail
          title="Primary Contact"
          icon={User}
          fullname={agencyContactName}
          email={agencyContactEmail}
          phone={agencyContactPhone}
        />

        {ConditionSeparator(isLoading, description)}
        <div id="contract-description">
          <Detail title="Description" icon={Text} details={[description]} />
        </div>
        <Separator />
        <Detail
          title="Attachments"
          icon={Paperclip}
          details={[
            <Attachments
              attachments={synopsisAttachmentFolders?.[0]?.synopsisAttachments}
              searchType="grant"
              isLoading={isLoading}
            />,
          ]}
        />
        <Separator />

        <Detail
          title={"Similar Active Opportunities"}
          icon={TextSearch}
          details={[
            <Recommendations searchType="grant" description={description} />,
          ]}
        />
      </div>
    </dl>
  );
}
